import React, { useCallback, useEffect } from 'react';
import CSSZSickLeaveCertificateForm from './CSSZSickLeaveCertificateForm';
import {
	CSSZAddress,
	CSSZEmployment,
	CSSZSickLeaveCertificateFormValues,
	CSSZUser,
} from '../../Interfaces/CSSZ.interface';
import useCSSZSickLeaveSender from '../Hooks/useCSSZSickLeaveSender';
import Loading from '../../../../Components/Loading';
import { DeepPartial } from '../../../../Interfaces/DeepPartial';

interface Props {
	user: CSSZUser;
	onCreate?: (sickLeaveDocumentId?: string) => void | Promise<void>;
	discourseDocumentId?: string;
}

export type CSSZSickLeaveSickLeaveSubmitPayload =
	CSSZSickLeaveCertificateFormValues & { submitToCssz?: boolean };

export type CSSZSubmitValues =
	| { type: 'patientAddress'; data: CSSZAddress }
	| { type: 'employment'; data: CSSZEmployment | undefined }
	| { type: 'sickLeave'; data: CSSZSickLeaveSickLeaveSubmitPayload };

export interface CSSZSickLeaveValues {
	patientAddress: CSSZAddress;
	employment: CSSZEmployment;
	sickLeave: CSSZSickLeaveSickLeaveSubmitPayload;
	decisionNumber?: string;
	discourseDocumentId?: string;
}

const CSSZSickLeaveCertificateCreate: React.FC<Props> = ({
	onCreate,
	user,
	discourseDocumentId,
}) => {
	const { collectedValues, setCollectedValues, requestSend, isLoading } =
		useCSSZSickLeaveSender(user.id, onCreate);
	useEffect(() => {
		if (!discourseDocumentId) {
			return;
		}

		setCollectedValues(prev => ({
			...prev,
			discourseDocumentId,
		}));
	}, [discourseDocumentId, setCollectedValues]);

	useEffect(() => {
		if (!user) return;
		setCollectedValues(prev => ({
			...prev,
			patientAddress: user?.profile?.address,
		}));
	}, [user, setCollectedValues]);

	const handleSubmitValues = useCallback(
		async (values: CSSZSubmitValues, send: boolean = false) => {
			let nextValues!: DeepPartial<CSSZSickLeaveValues>;

			setCollectedValues(prev => {
				nextValues = {
					...prev,
					[values.type]: values.data,
				};
				return nextValues;
			});

			if (send) {
				await requestSend(nextValues);
			}
		},
		[requestSend],
	);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<CSSZSickLeaveCertificateForm
			onSubmit={handleSubmitValues}
			collectedValues={collectedValues}
			user={user}
		/>
	);
};

export default CSSZSickLeaveCertificateCreate;
